import { Field } from './Field';
import { ImageField } from './ImageField';
import { useAdmin } from '../AdminContext';
import { fieldLabels, imageKeys, selectOptions, type FieldMeta } from '../adminLabels';
import { inputCls } from './styles';
import { fill } from '../../utils/copy';
import { wordTips } from '../../data/wordList';
import { LONG_TEXT, rowsFor, type Json } from './contentShape';
type Props = {
  fieldKey: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: Json) => void;
};

/**
 * Richtlengtes (docs/VORMGEVING.md › Woorden): een knop past op één regel, een kop op twee.
 * Het beheer telt mee en kleurt bij te lang; opslaan blijft altijd mogelijk.
 */
const MAX_LENGTH: Record<string, number> = {
  cta: 28,
  secondaryCta: 28,
  quizCta: 32,
  addCta: 24,
  salonCta: 32,
  askCta: 24,
  shopCta: 28,
  eyebrow: 50,
  text: 40,
  accent: 30,
  title: 48,
  why: 90
};

/** Velden die de site weglaat als ze leeg zijn, met wat Sarah dan ziet. */
const EMPTY_HIDES: Record<string, string> = {
  cta: 'Leeg: deze knop staat niet op de site.',
  secondaryCta: 'Leeg: deze knop staat niet op de site.',
  eyebrow: 'Leeg: er staat geen regel boven de kop.',
  body: 'Leeg: er staat geen tekst onder de kop.'
};

/** Gedachtestreepjes: de huisstijl gebruikt een punt, komma of dubbele punt. */
const DASH = /\s[—–]\s|—/;

/**
 * Eén tekstveld van de pagina-editor: een regel of een tekstvak, een beeld, een keuzelijst of een
 * product. Met "Op de site" (tokens ingevuld), de lengte, en zachte tips over de vaste woorden.
 */
export function ContentTextField({
  fieldKey,
  label,
  hint,
  value,
  onChange
}: Props) {
  const {
    catalog
  } = useAdmin();
  const base: FieldMeta | undefined = fieldLabels[fieldKey];
  const meta: FieldMeta | undefined = base ? {
    ...base,
    hint
  } : hint ? {
    label,
    hint
  } : undefined;
  const options = selectOptions[fieldKey];
  const max = MAX_LENGTH[fieldKey];
  const tips = wordTips(value);
  if (imageKeys.has(fieldKey)) {
    return <Field label={label} hint={meta?.hint} as="div" data-id="element-4988" data-mp="2fnckq4p2547v">
        <div className="max-w-[14rem]" data-id="element-4989" data-mp="2dbtm006cuxnw">
          <ImageField value={value} onChange={onChange} fit="cover" aspect="aspect-[4/3]" data-id="element-4990" data-mp="2d8nygpddk3ua" />
        </div>
      </Field>;
  }
  if (options) {
    return <Field label={label} hint={meta?.hint} data-id="element-4991" data-mp="24b56f11bcp8w">
        <select value={value} onChange={e => onChange(e.target.value)} className={`${inputCls} max-w-xs`} data-id="element-4992" data-mp="24bx4qjmap12v">
          {options.map(o => <option key={o.value} value={o.value} data-id="element-4993" data-mp="2wz15gcecut8u">
              {o.label}
            </option>)}
        </select>
      </Field>;
  }
  if (fieldKey === 'pickId') {
    const missing = !catalog.products.some(p => p.id === value);
    return <Field label={label} hint={meta?.hint} data-id="element-4994" data-mp="2n6llj9jhzulb">
        <select value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-4995" data-mp="2i8tvkl2eyijr">
          {missing && <option value={value} data-id="element-4996" data-mp="3fq0vzi1i0b48">Onbekend: {value || 'nog niets gekozen'}</option>}
          {catalog.products.map(p => <option key={p.id} value={p.id} data-id="element-4997" data-mp="3awfw1kgch72e">
              {p.brand} · {p.name}
            </option>)}
        </select>
        {/* Op de site valt zo'n keuze weg; zeg het hier, dan weet Sarah waarom. */}
        {missing && <span className="mt-1.5 block text-[12px] text-clay" data-id="element-4998" data-mp="3s84qijvg2agk">
            {value ? 'Dit product staat niet meer in de webshop. Op de site valt het weg.' : 'Kies een product; zonder keuze staat deze regel niet op de site.'}
          </span>}
      </Field>;
  }
  const long = meta?.long || value.length > LONG_TEXT;
  const filled = value.includes('{') ? fill(value) : null;
  return <Field label={label} hint={meta?.hint} data-id="element-4999" data-mp="et8epcaxq74q">
      {long ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={rowsFor(value)} className={`${inputCls} resize-y leading-relaxed`} data-id="element-5000" data-mp="ev1lqspgmtd9" /> : <input value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-5001" data-mp="os5w7v9y10e8" />}
      {filled && filled !== value && <span className="mt-1.5 block text-[12px] leading-snug text-muted" data-id="element-5002" data-mp="sqn3cetd6avn">
          <span className="text-sage" data-id="element-5003" data-mp="1qg1jkz7plxpz">Op de site:</span> {filled}
        </span>}
      {!value.trim() && EMPTY_HIDES[fieldKey] && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-5004" data-mp="1lgous8xa2gkr">{EMPTY_HIDES[fieldKey]}</span>}
      {max && value.trim() && <span className={`mt-1.5 block text-[12px] tabular-nums ${value.length > max ? 'text-clay' : 'text-muted'}`} data-id="element-5005" data-mp="1v7t00gx5in2e">
          {value.length}/{max} tekens{value.length > max ? ': past mogelijk niet op één regel' : ''}
        </span>}
      {DASH.test(value) && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-5006" data-mp="12m06kxxqwksa">Tip: een punt, komma of dubbele punt leest rustiger dan een gedachtestreepje.</span>}
      {tips.map(t => <span key={t} className="mt-1.5 block text-[12px] text-muted" data-id="element-5007" data-mp="1bj0pwq5yjsov">
          <span className="text-clay" data-id="element-5008" data-mp="2rxk1x17rvci8">Vaste woorden:</span> {t}
        </span>)}
    </Field>;
}